import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import AppLayout from '../AppLayout';
import { Section } from '../Section';
import { ErrorCard } from './ErrorCard';
import { Link, useNavigate } from 'react-router-dom';
import { PlaceholderNavbarTheme } from '../../../page/page.constants';
import { LogoTheme, Size } from '../../../../constants';
import { ThemeBackground, ThemePrimary, ThemeText } from '../../../../context/theme/ThemeConstantsUpdated';
import { RoundedSearchInput } from '../../inputs/RoundedSearchInput';
import { Logo } from '../../../navbar/Logo';
import { useAuth } from '../../../../context/auth/useAuth';
import { getUserHomePageUrl } from '../../../user/user.constants';
import { useSiteColors } from '../../../../hooks/useSiteColors';

const { mainBackground, secondaryBackground, borderBackground } = ThemeBackground;
const { mainText, secondaryText } = ThemeText;
const { primaryText, primaryTextHover } = ThemePrimary;

export const ErrorFallbackContainer = (props: Props) => {
  const { title, subTitle, error, resetErrorBoundary, resetLink, isNotFound } = props;
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const url = currentUser ? getUserHomePageUrl(currentUser) : '/';
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = useCallback(
    (query: string) => {
      navigate(`/search?filter=${encodeURIComponent(query)}`);
    },
    [navigate]
  );

  useEffect(() => {
    console.error('------------');
    console.error('error caught:');
    console.error(error);
    console.error('------------');
  }, [error]);

  if (isNotFound) {
    return (
      <AppLayout navbarThemeRevision={{ ...PlaceholderNavbarTheme, navbarSize: Size.none }} className={mainBackground}>
        <Section fullSpace className="relative h-screen max-w-none overflow-hidden !px-0">
          <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,_rgb(var(--user-primary-700))_0%,_rgb(var(--user-gray-1))_45%,_rgb(var(--user-gray-1))_100%)] opacity-80" />
          <div className="absolute inset-0 z-0 bg-[repeating-radial-gradient(circle_at_70%_15%,_rgba(var(--gray-200))_0px,_rgba(var(--gray-200))_1px,_transparent_3px,_transparent_18px)] opacity-30" />

          <div className="relative z-[1] flex h-full items-center justify-center px-4">
            <div className="w-full max-w-xl text-center">
              <div className="relative -top-6 flex items-center justify-center gap-2">
                <Logo size={Size.md} theme={LogoTheme.primary} options={{ customLink: url }} />
                <Link to={url} className="relative">
                  <h1 className={classNames(primaryText, 'mb-1 text-5xl font-bold tracking-tight md:text-6xl')}>
                    ReferralBook
                  </h1>
                </Link>
              </div>
              <h1 className={classNames(mainText, 'text-7xl font-semibold tracking-tight md:text-8xl')}>404</h1>
              <p className={classNames(secondaryText, 'mt-3 text-xl')}>Sorry, we were unable to find that page</p>

              <div className="mx-auto mt-7 w-full max-w-[34rem]">
                <RoundedSearchInput
                  outline
                  removeShadow
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onSubmit={handleSearch}
                  onClear={() => setSearchQuery('')}
                  placeholder="Search for deals, referrals, and more..."
                  classOverride={{
                    background: secondaryBackground,
                    border: borderBackground,
                  }}
                />
              </div>

              <button
                type="button"
                onClick={() => navigate('/')}
                className={classNames(
                  primaryText,
                  primaryTextHover,
                  'mt-5 text-sm font-medium underline-offset-4 hover:underline'
                )}
              >
                Start from home page
              </button>
            </div>
          </div>
        </Section>
      </AppLayout>
    );
  }

  return (
    <AppLayout navbarThemeRevision={{ ...PlaceholderNavbarTheme, navbarSize: Size.none }} className={mainBackground}>
      <Section fullSpace className="relative h-screen">
        <div className="fixed left-1/2 top-1/2 w-[32rem] -translate-x-1/2 -translate-y-1/2">
          <ErrorCard
            title={title}
            subTitle={subTitle}
            onReset={() => {
              resetErrorBoundary?.();
              resetLink != null ? navigate(resetLink) : navigate('/');
            }}
          />
        </div>
      </Section>
    </AppLayout>
  );
};

type Props = {
  error?: unknown;
  resetErrorBoundary?: () => void;
  title: string;
  subTitle: string;
  resetLink?: string;
  isNotFound?: boolean;
};
